import { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { cx } from '../../cx';
import { Icon } from '../../icon-svg/pc-icon';
import { GlobalImages } from '../../images';
import { NextModalProvider } from '../../next-modal';
import { useIsDarkMode } from '../../utils';
import { ApplySpaceAiBar } from '../components/apply-space-ai';
import { ChatInput } from '../components/chat-input';
import { ChatPageSkeleton } from '../components/skeleton';
import {
  fetchAiLimit,
  fetchAiSpaceStatus,
  useListenerUpgradeAiLimit,
} from '../hooks/ai-limit-store';
import { getEditor } from '../hooks/cache';
import { ConversationContext, useConversationContext } from '../hooks/context';
import { useChatStatus, useConversationEdit, useConversationTools } from '../hooks/hook';
import {
  fetchConversation,
  getChatStatus,
  getConversation,
  getConversationEdit,
} from '../hooks/store';
import type { ConversationContextType } from '../hooks/types';
import { AI_PAGE_ID, ChatStatus, SideBarType } from '../hooks/types';
import { useChatTranslation } from '../hooks/use-i18n';
import i18n from '../i18n/i18n';
import { ChatPage } from './chat-page';
import { LiteChatPage } from './lite-chat-page';
import { isMobile } from 'react-device-detect';

export const AIChatIndex = (props: ConversationContextType) => {
  const { conversationId = AI_PAGE_ID, spaceId, updateContext } = props;
  useEffect(() => {
    const edit = getConversationEdit(conversationId);
    const conversation = getConversation(conversationId);
    const status = getChatStatus(conversationId);
    void fetchAiLimit(spaceId);

    if (conversationId !== AI_PAGE_ID && (!edit || status === ChatStatus.DEFAULT)) {
      void fetchConversation(conversationId).then(({ _conversation, blocks }) => {
        if (_conversation.question) document.title = _conversation.question;
        props.onLoadConversation?.(_conversation);
        props.updateBlocks?.(blocks);
      });
    } else if (conversation.question) {
      document.title = conversation.question;
    }
    updateContext({ sideBarType: SideBarType.OUTLINE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, spaceId]);

  useEffect(() => {
    void fetchAiSpaceStatus(spaceId);
  }, [spaceId]);

  return (
    <I18nextProvider i18n={i18n}>
      <ConversationContext.Provider value={props}>
        <NextModalProvider>
          {isMobile ? (
            <MobileChatPage />
          ) : (
            <>{conversationId === AI_PAGE_ID ? <HomePage /> : <ChatPage />}</>
          )}
        </NextModalProvider>
      </ConversationContext.Provider>
    </I18nextProvider>
  );
};
const MobileChatPage = () => {
  useListenerUpgradeAiLimit();
  return <LiteChatPage />;
};

const HomePage = () => {
  useListenerUpgradeAiLimit();
  const { t } = useChatTranslation();
  const { keywords } = useConversationEdit();
  const { updateContext, conversationId, navbar } = useConversationContext();
  const status = useChatStatus(conversationId);
  const [showWriteRecommend, setShowWriteRecommend] = useState(false);
  const { summarize } = useConversationTools();
  useIsDarkMode();

  useEffect(() => {
    updateContext({ sideBarType: SideBarType.OUTLINE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!keywords) {
      setShowWriteRecommend(false);
    }
  }, [keywords]);

  const handleClick = (type: 'summary' | 'write') => {
    const editor = getEditor(conversationId);
    if (editor) {
      switch (type) {
        case 'summary':
          summarize();
          break;
        case 'write':
          setShowWriteRecommend(true);
          editor.commands.insertContent(t('chatHomeHelpMeWrite'));
          editor.commands.focus();
          break;
        default:
          break;
      }
    }
  };

  if (status === ChatStatus.LOADING) {
    return <ChatPageSkeleton />;
  }

  return (
    <div
      id={AI_PAGE_ID}
      className="relative p-10 sm:px-4 w-full max-w-3xl mx-auto text-t2 flex flex-col items-center justify-center pt-[20vh]"
    >
      {navbar?.()}
      <div className="flex flex-col items-center justify-center mb-8">
        <img src={GlobalImages.chatAiLogo} alt="chat-ai-logo" className="h-14 mb-2" />
        <div className="text-t1 text-center">{t('chatHomeDescription')}</div>
      </div>
      <div className="w-full space-y-4">
        <ApplySpaceAiBar provider="chat" />
        <div
          className={cx(
            'w-full py-2 px-4 rounded-lg dark:bg-grey9 border border-normal/15 transition-border focus-within:border-normal/35'
          )}
          style={{
            boxShadow: '0px 4px 12px 0px #0000000D',
          }}
        >
          <ChatInput />
        </div>
      </div>
      <div hidden={showWriteRecommend} className="flex items-center justify-center space-x-12 mt-8">
        {[
          {
            type: 'summary' as const,
            icon: 'IcSummary' as const,
            text: t('chatHomeSummarize'),
          },
          {
            type: 'write' as const,
            icon: 'IcMagicWand' as const,
            text: t('chatHomeWrite'),
          },
        ].map((item) => (
          <div
            key={item.type}
            className="space-y-2 text-t1 flex items-center flex-col justify-center animate-click group"
            onClick={() => handleClick(item.type)}
          >
            <span className="rounded-full bg-black_003 text-ai_color p-4 flex items-center group-hover:bg-black_006">
              <Icon name={item.icon} size="large" />
            </span>
            <div>{item.text}</div>
          </div>
        ))}
      </div>
      {showWriteRecommend && <WriteRecommend />}
    </div>
  );
};

const WriteRecommend = () => {
  const { conversationId } = useConversationContext();
  const { t } = useChatTranslation();

  const handleClick = (str: string) => {
    const editor = getEditor(conversationId);
    if (editor) {
      editor.commands.insertContent(str);
      editor.commands.focus();
    }
  };

  return (
    <div className="py-4 flex items-center w-full gap-x-2">
      {[
        t('chatWriteCoverLetter'),
        t('chatWriteBlogPost'),
        t('chatWriteScreenplay'),
        t('chatWriteBedtimeStory'),
      ].map((item) => (
        <span
          key={item}
          className="border rounded animate-click animate-hover py-1 px-2"
          onClick={() => handleClick(item)}
        >
          {item}
        </span>
      ))}
    </div>
  );
};
